/**
 * PostVisitSummaryDialog — read-only PVS render + Print/Download action.
 *
 * First encounter_module carve-out into PM (PROD-874, Epic PROD-873).
 * Design revision PROD-886 (Andrew 2026-05-21) — header bar + two-column
 * layout + section icons + 15-slot vitals grid + Scrips Community card.
 *
 * Flutter ref: `encounter_module/encounter_wrap_up/presentation/widgets/post_visit_summary_standalone.dart`
 *
 * Color rule: all decorative section icons use Signal DS brand-primary
 * (Blue/500) per the "use our color coding" directive. Medication-bucket
 * arrows keep the clinical-signal palette (success/warning/info/error)
 * carved out in PR 1.
 *
 * Vitals grid: 15 base slots + optional LMP (female + age > 8) + optional
 * HCM (age ≤ 2). Mirrors Flutter `emptyVitalUiList` in
 * `patient_module/patient_chart/data/datamodels/ui_data.dart:1184`.
 */

import { useState } from 'react';
import {
  ArrowDown,
  ArrowRight,
  BookOpen,
  CalendarDays,
  ClipboardList,
  Dna,
  Ear,
  Eye,
  FlaskConical,
  Heart,
  Ruler,
  StopCircle,
  Syringe,
  Scale,
  Thermometer,
  RefreshCw,
  Wind,
  Pill,
  Activity,
  Droplets,
  NotebookText,
  type LucideIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { isPracticeSwitchOn } from '@/shared/lib/practice-switches';
import { VITAL_CODES } from '@/scheduling/components/view-appointment-panel/vitals/vital-codes';
import {
  usePostVisitSummary,
  usePVSPrint,
  type PvsLookup,
} from '@/patients/services/use-post-visit-summary';
import type {
  PostVisitSummaryData,
  PvsArticle,
  PvsLab,
  PvsNarrativeSection,
  PvsProcedure,
  PvsRadiology,
  PvsStopTakingMedication,
  PvsTakingMedication,
  PvsVaccination,
} from '@/types/post-visit-summary';

export interface PostVisitSummaryDialogProps extends PvsLookup {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PostVisitSummaryDialog({
  open,
  onOpenChange,
  encounterId,
  appointmentId,
}: PostVisitSummaryDialogProps) {
  const enabled = open && !!(encounterId || appointmentId);

  const query = usePostVisitSummary(
    enabled ? { encounterId, appointmentId } : { encounterId: null },
  );
  const print = usePVSPrint();
  const [printErrored, setPrintErrored] = useState(false);

  const data = query.data ?? null;

  const handlePrint = () => {
    if (!encounterId && !appointmentId) return;
    setPrintErrored(false);
    print.mutate(
      { encounterId, appointmentId },
      { onError: () => setPrintErrored(true) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-[min(95vw,1100px)] sm:max-w-none max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
      >
        <DialogTitle className="sr-only">Post Visit Summary</DialogTitle>

        {/* Header bar — Done · centered title · Print. Mirrors Flutter
            `post_visit_summary_standalone.dart:120-178` exactly. */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-[var(--surface-line)]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-base font-semibold text-brand-primary hover:opacity-80 transition-opacity"
          >
            Done
          </button>
          <div className="flex-1 text-center min-w-0 px-4">
            <div className="text-base font-bold text-[var(--color-brand-depth)] truncate">
              {data?.visitReason?.trim() || ''}
            </div>
            <div className="text-xs text-text-muted truncate">
              {data?.encounterDate?.trim() || ''}
            </div>
          </div>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!data || print.isPending}
            className="text-base font-semibold text-brand-primary hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {print.isPending ? 'Preparing…' : 'Print'}
          </button>
        </header>

        <div className="overflow-y-auto scrollbar-hide flex-1 px-6 py-6 bg-grey-100/30">
          {query.isPending ? (
            <LoadingSkeleton />
          ) : query.isError ? (
            <EmptyBlock>Could not load the Post-Visit Summary. Try again later.</EmptyBlock>
          ) : !data ? (
            <EmptyBlock>No Post-Visit Summary is available for this encounter.</EmptyBlock>
          ) : (
            <PvsBody data={data} />
          )}
          {printErrored ? (
            <p className="mt-4 [font:var(--type-body)] text-[var(--status-bad)]">
              Could not open the PDF. Check your popup blocker and try again.
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Body ────────────────────────────────────────────────────────────────────

function PvsBody({ data }: { data: PostVisitSummaryData }) {
  // The signed template answers, behind ?x=pvs-visit-notes (off by default) until reviewed.
  const showVisitNotes = isPracticeSwitchOn('pvs-visit-notes');
  return (
    <div className="bg-[var(--surface-read)] rounded-card p-6 grid grid-cols-1 md:grid-cols-[1fr_304px] gap-8">
      {/* Left — main content */}
      <div className="space-y-6 min-w-0">
        <TitleBlock data={data} />
        <DoctorInfo data={data} />
        {showVisitNotes ? (
          <VisitNotesSection
            sections={data.templateNarrative ?? []}
            unavailable={data.templateNarrativeUnavailable === true}
          />
        ) : null}
        <InstructionsSection text={data.instructions ?? null} />
        <ImmunizationSection items={data.vaccinations ?? []} />
        <MedicationsSection
          start={data.startTakingMedications ?? []}
          change={data.changeTakingMedications ?? []}
          continueMeds={data.continueTakingMedications ?? []}
          stop={data.stopTakingMedications ?? []}
        />
        <FollowUpSection
          duration={data.wrapup?.followupDuration ?? null}
          date={data.wrapup?.followupDate ?? null}
        />
        <OrdersSection
          labs={data.labs ?? []}
          radiologies={data.radiologies ?? []}
          procedures={data.procedures ?? []}
        />
        <ArticlesSection items={data.articles ?? []} />
      </div>

      {/* Right — practice + today's visit + community */}
      <aside className="space-y-6">
        <PracticeSidebar data={data} />
        <TodaysVisitPanel data={data} />
        <ScripsCommunityCard />
      </aside>
    </div>
  );
}

function TitleBlock({ data }: { data: PostVisitSummaryData }) {
  const patient = data.patient ?? null;
  // Mirrors Flutter `Patient.displayInfo()`:
  //   "$patientName |  $mrn  |  DOB: ${dob}${age ? '  |  Age: ${age}' : ''}"
  const parts: string[] = [];
  if (patient?.patientName?.trim()) parts.push(patient.patientName.trim());
  if (patient?.mrn?.trim()) parts.push(patient.mrn.trim());
  if (patient?.dob?.trim()) parts.push(`DOB: ${patient.dob.trim()}`);
  if (patient?.age?.trim()) {
    // API may already include "y" suffix ("27y") or be bare ("27"). Strip
    // any trailing y/Y so we can append our own unit without doubling up.
    const ageNum = patient.age.trim().replace(/[yY]+$/, '');
    parts.push(`Age: ${ageNum}y`);
  }
  const displayInfo = parts.join('  |  ');

  const policy = data.policy?.trim();
  const eRx = data.eRx?.trim();

  return (
    <div>
      <h2 className="text-lg font-bold text-[var(--color-brand-depth)]">
        Post Visit Summary
      </h2>
      <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-2">
        {displayInfo || '—'}
      </p>
      {(policy || eRx) ? (
        <p className="[font:var(--type-body)] text-[var(--ink-body)]">
          {policy ? `| Member ID : ${policy}` : ''}
          {policy && eRx ? '  ' : ''}
          {eRx ? `| eRx # : ${eRx}` : ''}
        </p>
      ) : null}
    </div>
  );
}

function DoctorInfo({ data }: { data: PostVisitSummaryData }) {
  const provider = data.provider ?? null;
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-grey-100 overflow-hidden shrink-0">
        {provider?.providerPhoto ? (
          <img
            src={provider.providerPhoto}
            alt=""
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : null}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-brand-primary">
          {provider?.providerName?.trim() || '—'}
        </div>
        <div className="[font:var(--type-body)] text-[var(--ink-body)]">
          {provider?.providerSpeciality?.trim() || ''}
        </div>
      </div>
    </div>
  );
}

/**
 * The signed template answers (PROD-2531), as the printed PDF's "Visit Notes" shows them
 * (`PVSTemplate.html`): each answered section's title, then "Label: value" lines, text as
 * written. An entry with no value ("Allergies:" and nothing after it) is left out, and so is a
 * section left with no answered line. Nothing when the visit was documented without a template; whenever QuestionBank could not
 * be read (`templateNarrativeUnavailable`), a line saying the notes could not be loaded, also under
 * any sections that did come back. Read-only.
 */
function VisitNotesSection({
  sections,
  unavailable,
}: {
  sections: PvsNarrativeSection[];
  unavailable: boolean;
}) {
  const shown = sections
    .map((s) => ({
      title: s.title?.trim() ?? '',
      entries: (s.entries ?? []).filter((e) => e.value?.trim()),
    }))
    .filter((s) => s.entries.length > 0);
  if (shown.length === 0 && !unavailable) return null;
  return (
    <Section title="Visit notes" icon={NotebookText}>
      <div className="space-y-4">
        {shown.map((s, i) => (
          <div key={`${s.title}-${i}`}>
            {s.title ? <p className="text-sm font-semibold text-text-primary break-words">{s.title}</p> : null}
            <ul className="mt-1 space-y-1">
              {s.entries.map((e, j) => (
                <li key={`${e.label ?? ''}-${j}`} className="[font:var(--type-body)] text-[var(--ink-body)] whitespace-pre-wrap break-words">
                  {e.label?.trim() ? <span className="font-medium">{`${e.label.trim()}: `}</span> : null}
                  {e.value ?? ''}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {unavailable ? <EmptyText>Visit notes could not be loaded for this summary.</EmptyText> : null}
      </div>
    </Section>
  );
}

function InstructionsSection({ text }: { text: string | null }) {
  const value = text?.trim();
  return (
    <Section title="Instructions" icon={ClipboardList}>
      <RichTextBlock text={value} fallback="No instructions" />
    </Section>
  );
}

function ImmunizationSection({ items }: { items: PvsVaccination[] }) {
  return (
    <Section title="Immunization" icon={Syringe}>
      {items.length === 0 ? (
        <EmptyText>No administration</EmptyText>
      ) : (
        <ul className="space-y-3">
          {items.map((v, i) => (
            <li key={`${v.vaccinationCode ?? ''}-${i}`}>
              <p className="text-sm font-semibold text-text-primary">
                {`Administered Vaccine of ${v.vaccinationName?.trim() || '—'}`}
              </p>
              {(v.vaccinationDose?.trim() || v.vaccinationLotNo?.trim()) ? (
                <p className="[font:var(--type-body)] text-[var(--ink-body)]">
                  {v.vaccinationDose?.trim() ? `${v.vaccinationDose.trim()}, ` : ''}
                  {v.vaccinationLotNo?.trim()
                    ? `Lot no. ${v.vaccinationLotNo.trim()}`
                    : ''}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function MedicationsSection({
  start,
  change,
  continueMeds,
  stop,
}: {
  start: PvsTakingMedication[];
  change: PvsTakingMedication[];
  continueMeds: PvsTakingMedication[];
  stop: PvsStopTakingMedication[];
}) {
  const empty =
    start.length === 0 &&
    change.length === 0 &&
    continueMeds.length === 0 &&
    stop.length === 0;

  return (
    <Section title="Today's medication changes" icon={Pill}>
      {empty ? (
        <EmptyText>No changes</EmptyText>
      ) : (
        <div className="space-y-4">
          {start.length > 0 ? (
            <BucketBlock
              label="Start taking"
              tone="success"
              icon={ArrowRight}
              items={start}
            />
          ) : null}
          {change.length > 0 ? (
            <BucketBlock
              label="Change how you take"
              tone="warning"
              icon={RefreshCw}
              items={change}
            />
          ) : null}
          {continueMeds.length > 0 ? (
            <BucketBlock
              label="Continue taking"
              tone="info"
              icon={ArrowDown}
              items={continueMeds}
            />
          ) : null}
          {stop.length > 0 ? (
            <BucketBlock
              label="Stop taking"
              tone="danger"
              icon={StopCircle}
              items={stop.map((s) => ({
                name: s.name,
                description: s.stopReson,
              }))}
            />
          ) : null}
        </div>
      )}
    </Section>
  );
}

function BucketBlock({
  label,
  tone,
  icon: Icon,
  items,
}: {
  label: string;
  tone: 'success' | 'warning' | 'info' | 'danger';
  icon: LucideIcon;
  items: Array<{ name?: string | null; description?: string | null }>;
}) {
  // Clinical-signal carve-out for treatment intent (PR 1 review).
  const toneClass =
    tone === 'success'
      ? 'text-green-700'
      : tone === 'warning'
        ? 'text-amber-700'
        : tone === 'danger'
          ? 'text-[var(--status-bad)]'
          : 'text-blue-700';

  return (
    <div>
      <p className={`flex items-center gap-2 text-sm font-medium ${toneClass}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </p>
      <ul className="mt-2 space-y-2 pl-6">
        {items.map((m, i) => (
          <li key={`${m.name ?? ''}-${i}`}>
            <p className="text-sm font-semibold text-text-primary">
              {m.name?.trim() || '—'}
            </p>
            <p className="[font:var(--type-body)] text-[var(--ink-body)] whitespace-pre-wrap">
              {(m.description ?? '').replace(/<br\s*\/?\s*>/gi, '\n')}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FollowUpSection({
  duration,
  date,
}: {
  duration: string | null;
  date: string | null;
}) {
  const hasNothing = !duration && !date;
  return (
    <Section title="Come back for follow up" icon={CalendarDays}>
      {hasNothing ? (
        <EmptyText>No follow up</EmptyText>
      ) : (
        <p className="[font:var(--type-body)] text-[var(--ink-body)] whitespace-pre-line">
          {`In ${duration ?? '—'}  |  Around ${date ?? '—'}\nYou can schedule via Scrips app or reception.`}
        </p>
      )}
    </Section>
  );
}

function OrdersSection({
  labs,
  radiologies,
  procedures,
}: {
  labs: PvsLab[];
  radiologies: PvsRadiology[];
  procedures: PvsProcedure[];
}) {
  const rows: Array<{ kind: string; title: string; info: string | null }> = [
    ...labs.map((l) => ({
      kind: 'lab',
      title: `${l.labCode ?? ''} • ${l.labName ?? ''}`,
      info: l.labNotes ?? null,
    })),
    ...radiologies.map((r) => ({
      kind: 'radiology',
      title: `${r.radiologyCode ?? ''} • ${r.radiologyName ?? ''}`,
      info: r.radiologyNotes ?? null,
    })),
    ...procedures.map((p) => ({
      kind: 'procedure',
      title: `${p.procedureCode ?? ''} • ${p.procedureName ?? ''}`,
      info: p.procedureNotes ?? null,
    })),
  ];

  return (
    <Section title="Recommended orders" icon={FlaskConical}>
      {rows.length === 0 ? (
        <EmptyText>No orders</EmptyText>
      ) : (
        <ul className="space-y-3">
          {rows.map((r, i) => (
            <li key={`${r.kind}-${i}`}>
              <p className="text-sm font-semibold text-text-primary">
                {r.title.replace(/^ • | • $/g, '').trim() || '—'}
              </p>
              {r.info?.trim() ? (
                <RichTextBlock text={r.info} fallback="" />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function ArticlesSection({ items }: { items: PvsArticle[] }) {
  return (
    <Section title="Read the attached information" icon={BookOpen}>
      {items.length === 0 ? (
        <EmptyText>No information</EmptyText>
      ) : (
        <ul className="space-y-2">
          {items.map((a, i) => (
            <li key={`${a.articleUrl ?? ''}-${i}`}>
              {a.articleUrl?.trim() ? (
                <a
                  href={a.articleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-brand-primary hover:underline"
                >
                  {a.articleName?.trim() || a.articleUrl}
                </a>
              ) : (
                <span className="[font:var(--type-body)] text-[var(--ink-body)]">
                  {a.articleName?.trim() || '—'}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PracticeSidebar({ data }: { data: PostVisitSummaryData }) {
  return (
    <div className="text-right">
      <div className="flex justify-end mb-3">
        {data.organizationPhoto?.trim() ? (
          <img
            src={data.organizationPhoto}
            alt=""
            className="h-10 w-auto object-contain"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-grey-100" aria-hidden="true" />
        )}
      </div>
      <p className="text-sm font-bold text-text-primary">
        {data.practiceName?.trim() || '—'}
      </p>
      <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-1">
        {data.practiceAddress?.trim() || '—'}
      </p>
      {data.practicePhone?.trim() ? (
        <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-1">
          {data.practicePhone.trim()}
        </p>
      ) : null}
      {data.organizationName?.trim() ? (
        <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-3">
          {`Thank you for choosing ${data.organizationName.trim()}`}
        </p>
      ) : null}
    </div>
  );
}

function TodaysVisitPanel({ data }: { data: PostVisitSummaryData }) {
  const provider = data.provider?.providerName?.trim();
  const time = data.encounterTime?.trim();
  const date = data.encounterDate?.trim();
  const reason = data.visitReason?.trim();

  const narrative =
    provider && time && date && reason
      ? `You saw ${provider} at ${time} on ${date} for ${reason}.`
      : null;

  const diagnoses = (data.diagnoses ?? [])
    .map((d) => d.diagnosisName?.trim())
    .filter((s): s is string => Boolean(s));

  const vitalsValueByCode = new Map<string, string>();
  for (const v of data.vitals ?? []) {
    const code = v.vitalCode?.trim();
    const value = v.vitalValue?.trim();
    if (code && value) vitalsValueByCode.set(code, value);
  }
  // BP is one cell: "systolic/diastolic". With only one of the two it shows "—".
  const systolic = vitalsValueByCode.get(PVS_VITAL_CODES.systolic);
  const diastolic = vitalsValueByCode.get(PVS_VITAL_CODES.diastolic);
  if (systolic && diastolic) {
    vitalsValueByCode.set(PVS_VITAL_CODES.systolic, `${systolic}/${diastolic}`);
  } else {
    vitalsValueByCode.delete(PVS_VITAL_CODES.systolic);
  }

  const slots = buildVitalSlots({
    gender: data.patient?.genderName ?? null,
    age: parseAgeYears(data.patient?.age ?? null, data.patient?.dob ?? null),
  });

  return (
    <div className="rounded-card bg-grey-100/50 p-4">
      <h3 className="text-sm font-bold text-[var(--color-brand-depth)]">
        Today&apos;s Visit
      </h3>
      {narrative ? (
        <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-2">{narrative}</p>
      ) : null}
      {diagnoses.length > 0 ? (
        <>
          <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-2">
            The following issues were addressed:
          </p>
          <ul className="[font:var(--type-body)] text-[var(--ink-body)] mt-1 space-y-1">
            {diagnoses.map((d, i) => (
              <li key={i}>• {d}</li>
            ))}
          </ul>
        </>
      ) : null}

      <h4 className="text-sm font-medium text-[var(--color-brand-depth)] mt-6">
        Your vitals
      </h4>
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
        {slots.map((s) => {
          const value = vitalsValueByCode.get(s.code) ?? null;
          return <VitalCell key={s.code} slot={s} value={value} />;
        })}
      </ul>
    </div>
  );
}

function VitalCell({ slot, value }: { slot: VitalSlot; value: string | null }) {
  const Icon = slot.icon;
  return (
    <li className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-text-muted mt-1 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <div className="text-xs font-medium text-text-muted">
          {slot.label}
        </div>
        <div className="[font:var(--type-body)] text-[var(--ink-body)] mt-1">
          {value?.trim() ? value.trim() : '—'}
          {slot.unit ? <span className="text-text-muted"> {slot.unit}</span> : null}
        </div>
      </div>
    </li>
  );
}

function ScripsCommunityCard() {
  return (
    <div className="rounded-card bg-grey-100/50 p-4 relative">
      <h3 className="text-sm font-bold text-[var(--color-brand-depth)]">
        Be Part of Scrips Community
      </h3>
      <p className="[font:var(--type-body)] text-[var(--ink-body)] mt-2">
        Access your invoice and other medical documents in your Scrips
        account. We value your health beyond clinical visits. There is so
        much our community based 360 degrees of care can offer you.
      </p>
      <div
        className="text-sm font-bold text-brand-primary mt-4 text-right tracking-wider"
        aria-label="Scrips"
      >
        scrips
      </div>
    </div>
  );
}

// ─── Vitals slot config ──────────────────────────────────────────────────────

interface VitalSlot {
  code: string;
  label: string;
  unit: string;
  icon: LucideIcon;
}

/**
 * Build the canonical vitals slot list. Mirrors Flutter `emptyVitalUiList`
 * (`patient_module/patient_chart/data/datamodels/ui_data.dart:1184`).
 *
 * Base list is 15 slots; LMP adds when female + age > 8; HCM adds when
 * age ≤ 2. Codes are the wire `data.vitals[*].vitalCode` values (PVS_VITAL_CODES).
 */
function buildVitalSlots({
  gender,
  age,
}: {
  gender: string | null;
  age: number | null;
}): VitalSlot[] {
  const base: VitalSlot[] = [
    { code: PVS_VITAL_CODES.temp, label: 'Temp', unit: '°C', icon: Thermometer },
    { code: PVS_VITAL_CODES.pulse, label: 'Pulse', unit: '', icon: Heart },
    { code: PVS_VITAL_CODES.systolic, label: 'BP', unit: '', icon: Activity },
    { code: PVS_VITAL_CODES.respRate, label: 'Resp rate', unit: '', icon: Wind },
    { code: PVS_VITAL_CODES.o2Sat, label: 'O2 sat', unit: '%', icon: Droplets },
    { code: PVS_VITAL_CODES.height, label: 'Height', unit: 'cm', icon: Ruler },
    { code: PVS_VITAL_CODES.weight, label: 'Weight', unit: 'kg', icon: Scale },
    { code: PVS_VITAL_CODES.bmi, label: 'BMI', unit: '', icon: Activity },
    { code: PVS_VITAL_CODES.hip, label: 'Hip circumf', unit: 'cm', icon: Ruler },
    { code: PVS_VITAL_CODES.waist, label: 'Waist circu', unit: 'cm', icon: Ruler },
    { code: PVS_VITAL_CODES.waistToHip, label: 'Waist-to-hip', unit: '', icon: Ruler },
    { code: PVS_VITAL_CODES.bioage, label: 'Bioage', unit: '', icon: Dna },
    { code: PVS_VITAL_CODES.visual, label: 'Visual', unit: '', icon: Eye },
    { code: PVS_VITAL_CODES.hearing, label: 'Hearing', unit: '', icon: Ear },
  ];

  if (gender?.toLowerCase() === 'female' && age != null && age > 8) {
    base.push({ code: PVS_VITAL_CODES.lmp, label: 'LMP', unit: '', icon: CalendarDays });
  }
  if (age != null && age <= 2) {
    base.push({ code: PVS_VITAL_CODES.hcm, label: 'HCM', unit: 'cm', icon: Ruler });
  }
  return base;
}

/**
 * The wire `vitalCode` values. Patient `PostVisitSummaryService` sends each
 * vital's LOINC code (`VitalCode = QuantityCode`) and appends its computed
 * BMI as the literal `"BMI"`; the server PDF (`TemplateToPdf.cs`) reads the
 * same codes. The earlier word codes ('temp', 'height', ...) never matched,
 * so every cell showed "—".
 */
const PVS_VITAL_CODES = {
  temp: VITAL_CODES.TEMPERATURE,
  pulse: VITAL_CODES.HEART_RATE,
  systolic: VITAL_CODES.SYSTOLIC,
  diastolic: VITAL_CODES.DIASTOLIC,
  respRate: VITAL_CODES.RESP_RATE,
  o2Sat: VITAL_CODES.O2_SAT,
  height: VITAL_CODES.HEIGHT,
  weight: VITAL_CODES.WEIGHT,
  bmi: 'BMI',
  hip: VITAL_CODES.HIP,
  waist: VITAL_CODES.WAIST,
  waistToHip: VITAL_CODES.WAIST_TO_HIP_RATIO,
  bioage: VITAL_CODES.BIOAGE,
  visual: VITAL_CODES.VISUAL,
  hearing: VITAL_CODES.HEARING,
  lmp: VITAL_CODES.LMP,
  hcm: VITAL_CODES.HCM,
} as const;

/**
 * Resolve patient age in years from either the API's `age` string
 * (which may be `"27y"` / `"27"` / `"36"`) or fallback to DOB.
 */
function parseAgeYears(
  ageStr: string | null,
  dob: string | null,
): number | null {
  if (ageStr?.trim()) {
    const m = /^(\d+)/.exec(ageStr.trim());
    if (m) return Number(m[1]);
  }
  if (dob?.trim()) {
    const t = Date.parse(dob);
    if (!Number.isNaN(t)) {
      const years = (Date.now() - t) / (1000 * 60 * 60 * 24 * 365.25);
      return Math.floor(years);
    }
  }
  return null;
}

// ─── Primitives ──────────────────────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="flex items-center gap-2 text-sm font-medium text-[var(--color-brand-depth)] mb-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[var(--accent-active-bg)]">
          <Icon className="h-4 w-4 text-brand-primary" aria-hidden="true" />
        </span>
        {title}
      </h3>
      <div className="pl-10">{children}</div>
    </section>
  );
}

function EmptyText({ children }: { children: React.ReactNode }) {
  return <p className="[font:var(--type-body)] text-[var(--ink-meta)]">{children}</p>;
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-[var(--surface-read)] rounded-card border border-dashed border-[var(--surface-line)] p-8 text-center">
      <p className="[font:var(--type-body)] text-[var(--ink-meta)]">{children}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="bg-[var(--surface-read)] rounded-card p-6 space-y-4">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-72" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

/**
 * Plain-text body renderer. Flutter renders this via `QuillStaticView`
 * which we don't ship in PM — strip `<br>` tags to newlines and call
 * it a day. If `instructions` ever ships full HTML, route through
 * `dompurify` (already used elsewhere). For now the wire is plain text
 * or a tiny set of `<br>` tags per Flutter usage.
 */
function RichTextBlock({
  text,
  fallback,
}: {
  text: string | null | undefined;
  fallback: string;
}) {
  const value = (text ?? '').trim();
  if (!value) {
    return fallback ? <EmptyText>{fallback}</EmptyText> : null;
  }
  return (
    <p className="[font:var(--type-body)] text-[var(--ink-body)] whitespace-pre-wrap">
      {value.replace(/<br\s*\/?\s*>/gi, '\n')}
    </p>
  );
}
